import { type ChangeEvent, type HTMLAttributes, type ReactNode, useId } from "react";
import { Field } from "react-final-form";
import { tw } from "../../styling";
import type { BaseFieldProps, SimpleFieldValue } from "../../types/form";
import { composeValidators } from "../../utils/validation";
import { TextInput as BaseTextInput } from "./components/text-input";
import { FormHelperText } from "./form-helper-text";

type Props<T1 extends SimpleFieldValue, T2 = T1> = BaseFieldProps<T1, T2> & {
	type?: "tel" | "email" | "text" | "number" | "password";
	inputMode?: HTMLAttributes<string>["inputMode"];
	label: string;
	id?: string;
	helperText?: string;
	handleChange?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
	disabled?: boolean;
	fullWidth?: boolean;
	endAdornment?: ReactNode;
	onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
};

export function TextField<T extends SimpleFieldValue = string>({
	id,
	label,
	type = "text",
	inputMode,
	name,
	validators,
	helperText,
	placeholder,
	handleChange,
	onBlur,
	endAdornment,
	disabled = false,
	fullWidth = true,
	maxLength,
	autoComplete,
	autoFocus = false,
	onKeyDown,
	...otherProps
}: Props<T, string>) {
	const describedBy = useId();
	return (
		<Field<T, HTMLElement, string>
			name={name}
			validate={validators && composeValidators(...validators)}
			{...otherProps}
		>
			{({ input, meta }) => {
				const hasError = (!!meta.error && !!meta.touched) || !!meta.submitError;

				const handleBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
					onBlur?.(e);
					input.onBlur();
				};

				const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
					handleChange?.(e);
					input.onChange(e);
				};

				return (
					<div className={tw("flex flex-col gap-2", fullWidth && "w-full")}>
						<BaseTextInput
							{...input}
							value={input.value?.toString() || ""}
							id={id}
							fullWidth={fullWidth}
							type={type}
							inputMode={inputMode}
							placeholder={placeholder}
							label={label}
							error={hasError}
							disabled={disabled}
							onKeyDown={onKeyDown}
							onChange={onChange}
							endAdornment={endAdornment}
							maxLength={maxLength}
							autoComplete={autoComplete}
							onBlur={handleBlur}
							autoFocus={autoFocus}
							aria-describedby={describedBy}
							aria-invalid={hasError}
						/>
						<FormHelperText error={hasError} id={describedBy}>
							{hasError ? meta.error || meta.submitError : helperText}
						</FormHelperText>
					</div>
				);
			}}
		</Field>
	);
}
